import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import {
  RiArrowLeftLine,
  RiBusLine,
  RiUserLine,
  RiCalendarLine,
  RiMapPinLine,
  RiCloseLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiMoneyRupeeCircleLine,
  RiAddLine,
  RiSubtractLine,
  RiEditLine,
  RiMapPinTimeLine,
  RiTimeLine,
} from "react-icons/ri";
import BusLayout from "./BusLayouts/BusLayout";
import BusBoardingPoints from "./BusBoardingPoints";
import "./BusSeatSelection.css";

const BusSeatSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bus, searchQuery } = location.state || {};

  const [seatLayoutData, setSeatLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerCount, setPassengerCount] = useState(1);
  const [showBoardingPoints, setShowBoardingPoints] = useState(false);
  const [selectedBoardingPoint, setSelectedBoardingPoint] = useState(null);
  const [selectedDroppingPoint, setSelectedDroppingPoint] = useState(null);
  const [blockingInProgress, setBlockingInProgress] = useState(false);
  const [passengerDetails, setPassengerDetails] = useState([]);

  const isUpdating = useRef(false);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-IN", options);
  };

  // Format time function
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    if (!bus || !searchQuery) {
      navigate("/bus/results");
      return;
    }
    fetchSeatLayout();
  }, [bus, searchQuery]);

  useEffect(() => {
    const initialDetails = Array.from({ length: passengerCount }, () => ({
      firstName: "",
      lastName: "",
      age: "",
      title: "Mr",
      idProof: "",
      idProofNumber: "",
      email: user?.email || "",
      phone: user?.phoneNumber || "",
    }));
    setPassengerDetails(initialDetails);
  }, []);

  // Effect to handle seat selection changes
  useEffect(() => {
    if (isUpdating.current) return;

    // When all seats are deselected, clear boarding points
    if (selectedSeats.length === 0) {
      setSelectedBoardingPoint(null);
      setSelectedDroppingPoint(null);
    }

    if (selectedSeats.length !== passengerCount) {
      isUpdating.current = true;

      // Update passenger count to match selected seats
      setPassengerCount(selectedSeats.length);

      if (selectedSeats.length > passengerDetails.length) {
        // Add new passenger forms
        const passengersToAdd = selectedSeats.length - passengerDetails.length;
        const newPassengers = Array.from({ length: passengersToAdd }, () => ({
          firstName: "",
          lastName: "",
          age: "",
          title: "Mr",
          idProof: "",
          idProofNumber: "",
          email: user?.email || "",
          phone: user?.phoneNumber || "",
        }));
        setPassengerDetails((prev) => [...prev, ...newPassengers]);
      } else if (selectedSeats.length < passengerDetails.length) {
        // Remove extra passenger forms
        setPassengerDetails((prev) => prev.slice(0, selectedSeats.length));
      }

      setTimeout(() => {
        isUpdating.current = false;
      }, 100);
    }
  }, [selectedSeats]);

  const fetchSeatLayout = async () => {
    setLoading(true);
    try {
      const requestData = {
        CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        Source: searchQuery.fromStation,
        Destination: searchQuery.toStation,
        DateOfJourney: searchQuery.travelDate,
        InventoryType: bus.inventoryType,
        RouteScheduleId: bus.routeScheduleId,
      };
      const url = `${config.GETBUSSEATS}/${bus.supplierId}`;

      const response = await apiService.post(url, requestData);
      if (response) {
        setSeatLayoutData(response);
      } else {
        setError("Failed to load seat layout. Please try again.");
      }
    } catch (err) {
      setError("Failed to fetch seat layout. Please try again.");
      console.error("Seat layout error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeatSelect = (seat) => {
    if (!seat.available || seat.bookedBy) return;

    const isAlreadySelected = selectedSeats.some((s) => s.id === seat.id);

    if (isAlreadySelected) {
      const newSelectedSeats = selectedSeats.filter((s) => s.id !== seat.id);
      setSelectedSeats(newSelectedSeats);

      // Clear boarding points only if ALL seats are deselected
      if (newSelectedSeats.length === 0) {
        setSelectedBoardingPoint(null);
        setSelectedDroppingPoint(null);
      }
    } else {
      const newSelectedSeats = [...selectedSeats, seat];
      setSelectedSeats(newSelectedSeats);
    }
  };

  const handlePassengerCountChange = (count) => {
    if (count < 1 || count > 6) return;

    isUpdating.current = true;

    if (count < passengerCount) {
      setPassengerCount(count);

      // If reducing passenger count, also reduce selected seats if needed
      if (selectedSeats.length > count) {
        setSelectedSeats((prev) => prev.slice(0, count));
      }

      // Reduce passenger details
      setPassengerDetails((prev) => prev.slice(0, count));
    } else if (count > passengerCount) {
      setPassengerCount(count);

      // Add new passenger details
      const passengersToAdd = count - passengerDetails.length;
      const newPassengers = Array.from({ length: passengersToAdd }, () => ({
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
      }));

      setPassengerDetails((prev) => [...prev, ...newPassengers]);
    }

    setTimeout(() => {
      isUpdating.current = false;
    }, 100);
  };

  const handlePassengerDetailChange = (index, field, value) => {
    const updatedDetails = [...passengerDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };
    setPassengerDetails(updatedDetails);
  };

  const handleEmailChange = (email) => {
    const updatedDetails = passengerDetails.map((detail) => ({
      ...detail,
      email: email,
    }));
    setPassengerDetails(updatedDetails);
  };

  const handlePhoneChange = (phone) => {
    const updatedDetails = passengerDetails.map((detail) => ({
      ...detail,
      phone: phone,
    }));
    setPassengerDetails(updatedDetails);
  };

  const areAllPassengerDetailsFilled = () => {
    return passengerDetails.every(
      (passenger) =>
        passenger.firstName &&
        passenger.lastName &&
        passenger.age &&
        passenger.idProof &&
        passenger.email &&
        passenger.phone
    );
  };

  const calculateBaseFare = () => {
    return selectedSeats.reduce((total, seat) => {
      const baseFare = seat.totalPriceWithTaxes || 0;
      return total + (parseFloat(baseFare) || 0);
    }, 0);
  };

  const calculateConvenienceFee = () => {
    return selectedSeats.reduce((total, seat) => {
      const convenienceFee = seat.convenienceFee || 100;
      return total + (parseFloat(convenienceFee) || 100);
    }, 0);
  };

  const calculateTotal = () => {
    return selectedSeats.reduce((total, seat) => {
      const seatTotal =
        (seat.totalPriceWithTaxes || 0) + (seat.convenienceFee || 100);
      return total + (parseFloat(seatTotal) || 0);
    }, 0);
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num).toLocaleString("en-IN")}`;
  };

  const handleChangeBoardingPoints = () => {
    setShowBoardingPoints(true);
  };

  const handleBoardingPointsSubmit = async (boardingPoint, droppingPoint) => {
    setSelectedBoardingPoint(boardingPoint);
    setSelectedDroppingPoint(droppingPoint);
    setShowBoardingPoints(false);
  };

  const handleBoardingPointsClose = () => {
    setShowBoardingPoints(false);
  };

  const handleProceedToPayment = () => {
    if (!selectedBoardingPoint || !selectedDroppingPoint) {
      alert("Please select both boarding and dropping points first.");
      return;
    }

    navigate("/bus/payment", {
      state: {
        bus,
        searchQuery,
        selectedSeats,
        passengerCount,
        passengerDetails,
        selectedBoardingPoint,
        selectedDroppingPoint,
        totalAmount: calculateTotal(),
        baseFare: calculateBaseFare(),
        convenienceFee: calculateConvenienceFee(),
      },
    });
  };

  // Clear boarding points when seats are cleared
  const handleClearAllSeats = () => {
    setSelectedSeats([]);
    setSelectedBoardingPoint(null);
    setSelectedDroppingPoint(null);
    setPassengerCount(1);
    setPassengerDetails([
      {
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
      },
    ]);
  };

  if (!bus || !searchQuery) {
    return (
      <div id="layout-wrapper">
        <Header />
        <div className="main-content">
          <div className="container-fluid">
            <div className="alert alert-danger">
              Invalid bus selection. Please go back and try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="layout-wrapper">
      <Header />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Header Section */}
            <div
              className="seat-selection-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "30px",
              }}
            >
              <h1 style={{ margin: 0 }}>Select Your Seats</h1>
              <button
                className="back-btn"
                onClick={() => navigate(-1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                <RiArrowLeftLine />
                Back to Buses
              </button>
            </div>

            {/* Bus Info Card */}
            <div className="bus-info-card">
              <div className="bus-info-grid">
                <div className="info-item">
                  <RiBusLine className="info-icon" />
                  <div className="info-content">
                    <div className="info-label">Bus Operator</div>
                    <div className="info-value">{bus.operatorName}</div>
                  </div>
                </div>
                <div className="info-item">
                  <RiMapPinLine className="info-icon" />
                  <div className="info-content">
                    <div className="info-label">Route</div>
                    <div className="info-value">
                      {searchQuery.fromStation} → {searchQuery.toStation}
                    </div>
                  </div>
                </div>
                <div className="info-item">
                  <RiCalendarLine className="info-icon" />
                  <div className="info-content">
                    <div className="info-label">Travel Date</div>
                    <div className="info-value">
                      <div className="date-time-display">
                        <span className="date-display">
                          {formatDate(searchQuery.travelDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="info-item">
                  <RiUserLine className="info-icon" />
                  <div className="info-content">
                    <div className="info-label">Passengers</div>
                    <div className="passenger-controls-compact">
                      <button
                        onClick={() =>
                          handlePassengerCountChange(passengerCount - 1)
                        }
                        disabled={passengerCount <= 1}
                      >
                        <RiSubtractLine />
                      </button>
                      <span>{passengerCount}</span>
                      <button
                        onClick={() =>
                          handlePassengerCountChange(passengerCount + 1)
                        }
                        disabled={passengerCount >= 6}
                      >
                        <RiAddLine />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="seat-selection-layout">
              <div className="layout-main">
                <div className="layout-card">
                  <div className="layout-header">
                    <h2>
                      <span className="seat-icon">💺</span>
                      Seats - {bus.busType}
                    </h2>
                    <div className="d-flex align-items-center gap-3">
                      <div className="selection-badge">
                        {selectedSeats.length} Selected
                      </div>
                      {selectedSeats.length > 0 && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={handleClearAllSeats}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="layout-content">
                    {loading && (
                      <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading seats...</p>
                      </div>
                    )}

                    {error && (
                      <div className="error-state">
                        <p>{error}</p>
                        <button onClick={fetchSeatLayout} className="retry-btn">
                          Try Again
                        </button>
                      </div>
                    )}

                    {!loading && !error && seatLayoutData && (
                      <div className="seat-layout-with-passengers">
                        {/* Seat Legend */}
                        <div className="seat-legend-display">
                          <div className="seat-legend-title">Seat Legend</div>
                          <div className="seat-legend-items">
                            <div className="seat-legend-item">
                              <div className="legend-seat available"></div>
                              <span>Available</span>
                            </div>
                            <div className="seat-legend-item">
                              <div className="legend-seat selected">✓</div>
                              <span>Selected</span>
                            </div>
                            <div className="seat-legend-item">
                              <div className="legend-seat booked">✗</div>
                              <span>Booked</span>
                            </div>
                            <div className="seat-legend-item">
                              <div className="legend-seat ladies">♀</div>
                              <span>Ladies Seat</span>
                            </div>
                          </div>
                        </div>

                        {/* Seat Layout */}
                        <div className="seat-layout-section">
                          <BusLayout
                            seatData={seatLayoutData}
                            onSeatSelect={handleSeatSelect}
                            selectedSeats={selectedSeats}
                          />
                        </div>

                        {/* Passenger Details Forms - Always show if seats are selected */}
                        {(selectedSeats.length > 0 || passengerCount > 0) && (
                          <div className="passenger-forms-section">
                            <div className="passenger-header">
                              <h4 className="passenger-forms-title">
                                <RiUserLine className="me-2" />
                                Passenger Details ({passengerCount}{" "}
                                {passengerCount === 1
                                  ? "Passenger"
                                  : "Passengers"}
                                )
                              </h4>
                              <div className="passenger-sync-info">
                                {selectedSeats.length !== passengerCount && (
                                  <div className="sync-alert">
                                    <RiErrorWarningLine />
                                    <span>
                                      Seats: {selectedSeats.length} |
                                      Passengers: {passengerCount}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="passenger-forms-grid">
                              {passengerDetails.map((passenger, index) => (
                                <div
                                  key={index}
                                  className="passenger-form-card"
                                >
                                  <div className="passenger-form-header">
                                    <span>Passenger {index + 1}</span>
                                    <span className="passenger-seat-info">
                                      {selectedSeats[index]
                                        ? `Seat: ${selectedSeats[index].id}`
                                        : "No seat assigned"}
                                    </span>
                                  </div>

                                  <div className="passenger-form-row">
                                    <div className="form-group">
                                      <label className="form-label">
                                        Title *
                                      </label>
                                      <select
                                        className="form-select"
                                        value={passenger.title}
                                        onChange={(e) =>
                                          handlePassengerDetailChange(
                                            index,
                                            "title",
                                            e.target.value
                                          )
                                        }
                                        required
                                      >
                                        <option value="Mr">Mr</option>
                                        <option value="Ms">Ms</option>
                                        <option value="Mrs">Mrs</option>
                                      </select>
                                    </div>

                                    <div className="form-group">
                                      <label className="form-label">
                                        First Name *
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="First name"
                                        value={passenger.firstName}
                                        onChange={(e) =>
                                          handlePassengerDetailChange(
                                            index,
                                            "firstName",
                                            e.target.value
                                          )
                                        }
                                        required
                                      />
                                    </div>

                                    <div className="form-group">
                                      <label className="form-label">
                                        Last Name *
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Last name"
                                        value={passenger.lastName}
                                        onChange={(e) =>
                                          handlePassengerDetailChange(
                                            index,
                                            "lastName",
                                            e.target.value
                                          )
                                        }
                                        required
                                      />
                                    </div>

                                    <div className="form-group">
                                      <label className="form-label">
                                        Age *
                                      </label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Age"
                                        min="1"
                                        max="100"
                                        value={passenger.age}
                                        onChange={(e) =>
                                          handlePassengerDetailChange(
                                            index,
                                            "age",
                                            e.target.value
                                          )
                                        }
                                        required
                                      />
                                    </div>

                                    <div className="form-group">
                                      <label className="form-label">
                                        ID Proof *
                                      </label>
                                      <select
                                        className="form-select"
                                        value={passenger.idProof}
                                        onChange={(e) =>
                                          handlePassengerDetailChange(
                                            index,
                                            "idProof",
                                            e.target.value
                                          )
                                        }
                                        required
                                        style={{ width: "100%" }}
                                      >
                                        <option value="">
                                          Select ID Proof
                                        </option>
                                        <option value="aadhar">
                                          Aadhar Card
                                        </option>
                                        <option value="pan">PAN Card</option>
                                        <option value="dl">
                                          Driving License
                                        </option>
                                        <option value="voter">Voter ID</option>
                                        <option value="passport">
                                          Passport
                                        </option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Common Email and Phone - ALWAYS SHOW */}
                        <div className="common-contact-fields mt-4">
                          <h5 className="common-contact-title mb-3">
                            <RiUserLine className="me-2" />
                            Contact Information
                          </h5>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Email Address *
                                </label>
                                <input
                                  type="email"
                                  className="form-control"
                                  placeholder="Enter email address"
                                  value={
                                    passengerDetails[0]?.email ||
                                    user?.email ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    handleEmailChange(e.target.value)
                                  }
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Phone Number *
                                </label>
                                <input
                                  type="tel"
                                  className="form-control"
                                  placeholder="Enter phone number"
                                  value={
                                    passengerDetails[0]?.phone ||
                                    user?.phoneNumber ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    handlePhoneChange(e.target.value)
                                  }
                                  required
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="booking-sidebar">
                <div className="summary-card">
                  <div className="summary-header">
                    <h3>Booking Summary</h3>
                  </div>

                  {/* Selected Points Display - Only show if seats are selected */}
                  {selectedSeats.length > 0 &&
                  selectedBoardingPoint &&
                  selectedDroppingPoint ? (
                    <div className="selected-points-display">
                      <div className="selected-points-header">
                        <h5 className="mb-0 d-flex align-items-center">
                          <RiMapPinTimeLine className="me-2" />
                          Selected Points
                        </h5>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleChangeBoardingPoints}
                        >
                          <RiEditLine className="me-1" />
                          Change
                        </button>
                      </div>
                      <div className="selected-points-details">
                        <div className="point-item">
                          <div className="point-label">Boarding Point</div>
                          <div className="point-value">
                            {selectedBoardingPoint.pointName}
                          </div>
                          <div className="text-muted small">
                            {formatTime(selectedBoardingPoint.time)}
                          </div>
                        </div>
                        <div className="point-arrow"></div>
                        <div className="point-item">
                          <div className="point-label">Dropping Point</div>
                          <div className="point-value">
                            {selectedDroppingPoint.pointName}
                          </div>
                          <div className="text-muted small">
                            {formatTime(selectedDroppingPoint.time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : selectedSeats.length > 0 ? (
                    <div className="selected-points-display">
                      <div className="selected-points-header">
                        <h5 className="mb-0 d-flex align-items-center">
                          <RiMapPinTimeLine className="me-2" />
                          Boarding Points
                        </h5>
                      </div>
                      <div className="text-center py-3">
                        <p className="text-muted mb-2">
                          Please select boarding and dropping points
                        </p>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowBoardingPoints(true)}
                        >
                          Select Points
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="summary-section">
                    <h4>Selected Seats</h4>
                    {selectedSeats.length > 0 ? (
                      <div className="selected-seats">
                        {selectedSeats.map((seat, index) => {
                          const isUpper = seat.seatType === 1;
                          const isSleeper = seat.sleeper;
                          const seatBaseFare = seat.totalPriceWithTaxes || 0;
                          const seatConvenienceFee = seat.convenienceFee || 100;
                          const seatTotal = seatBaseFare + seatConvenienceFee;
                          const passengerName = passengerDetails[index]
                            ? `${passengerDetails[index].firstName} ${passengerDetails[index].lastName}`
                            : `Passenger ${index + 1}`;

                          return (
                            <div key={seat.id} className="selected-seat">
                              <div className="seat-details">
                                <span className="seat-id">{seat.id}</span>
                                <div>
                                  <div className="seat-type">
                                    {isSleeper
                                      ? isUpper
                                        ? "Upper Sleeper"
                                        : "Lower Sleeper"
                                      : "Seater"}
                                  </div>
                                  <small className="text-muted">
                                    {passengerName}
                                  </small>
                                </div>
                              </div>
                              <div className="seat-price-actions">
                                <span className="price">
                                  {formatCurrency(seatTotal)}
                                </span>
                                <button
                                  className="remove-btn"
                                  onClick={() => handleSeatSelect(seat)}
                                  title="Remove seat"
                                >
                                  <RiCloseLine />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="no-seats">No seats selected yet</p>
                    )}
                  </div>

                  {selectedSeats.length > 0 && (
                    <>
                      <div className="passenger-sync-card">
                        {selectedSeats.length !== passengerDetails.length && (
                          <div className="sync-actions">
                            <p className="sync-message">
                              <RiErrorWarningLine className="text-warning me-2" />
                              Seats and forms count don't match
                            </p>
                            <div className="sync-buttons">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  if (
                                    selectedSeats.length >
                                    passengerDetails.length
                                  ) {
                                    const formsToAdd =
                                      selectedSeats.length -
                                      passengerDetails.length;
                                    const newPassengers = Array.from(
                                      { length: formsToAdd },
                                      () => ({
                                        firstName: "",
                                        lastName: "",
                                        age: "",
                                        title: "Mr",
                                        idProof: "",
                                        idProofNumber: "",
                                        email: user?.email || "",
                                        phone: user?.phoneNumber || "",
                                      })
                                    );
                                    setPassengerDetails((prev) => [
                                      ...prev,
                                      ...newPassengers,
                                    ]);
                                  } else {
                                    setPassengerDetails((prev) =>
                                      prev.slice(0, selectedSeats.length)
                                    );
                                  }
                                  setPassengerCount(selectedSeats.length);
                                }}
                              >
                                Sync Forms to {selectedSeats.length}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="summary-section">
                        <h4>Fare Breakdown</h4>
                        <div className="fare-details">
                          <div className="fare-line">
                            <span>Bus Fare </span>
                            <span>{formatCurrency(calculateBaseFare())}</span>
                          </div>
                          <div className="fare-line">
                            <span>Convenience Fee (INR100 per seat)</span>
                            <span>
                              {formatCurrency(calculateConvenienceFee())}
                            </span>
                          </div>
                          <div className="fare-divider"></div>
                          <div className="fare-line total">
                            <strong>Total Amount to Pay</strong>
                            <strong>{formatCurrency(calculateTotal())}</strong>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Conditional Button Logic */}
                  {selectedSeats.length === 0 ? (
                    <button className="proceed-btn disabled" disabled>
                      Select Seats to Continue
                    </button>
                  ) : selectedSeats.length !== passengerDetails.length ? (
                    <button className="proceed-btn disabled" disabled>
                      Sync Seats & Forms
                    </button>
                  ) : !areAllPassengerDetailsFilled() ? (
                    <button className="proceed-btn disabled" disabled>
                      Fill Passenger Details
                    </button>
                  ) : !selectedBoardingPoint || !selectedDroppingPoint ? (
                    <button
                      className="proceed-btn active"
                      onClick={() => setShowBoardingPoints(true)}
                    >
                      Select Boarding Points
                    </button>
                  ) : (
                    <button
                      className="proceed-btn active"
                      onClick={handleProceedToPayment}
                    >
                      Continue to Payment
                    </button>
                  )}

                  {/* Warning Messages */}
                  {selectedSeats.length > 0 &&
                    selectedSeats.length !== passengerDetails.length && (
                      <div className="warning-message">
                        <RiErrorWarningLine className="me-2" />
                        {selectedSeats.length > passengerDetails.length
                          ? `Missing ${
                              selectedSeats.length - passengerDetails.length
                            } passenger form${
                              selectedSeats.length - passengerDetails.length > 1
                                ? "s"
                                : ""
                            }`
                          : `Extra ${
                              passengerDetails.length - selectedSeats.length
                            } passenger form${
                              passengerDetails.length - selectedSeats.length > 1
                                ? "s"
                                : ""
                            }`}
                      </div>
                    )}

                  {selectedSeats.length > 0 &&
                    selectedSeats.length === passengerDetails.length &&
                    !areAllPassengerDetailsFilled() && (
                      <div className="warning-message">
                        <RiErrorWarningLine className="me-2" />
                        Please fill all passenger details to continue
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Boarding Points Modal */}
      {showBoardingPoints && (
        <BusBoardingPoints
          bus={bus}
          onClose={handleBoardingPointsClose}
          onSubmit={handleBoardingPointsSubmit}
          autoProceed={false}
        />
      )}

      {/* Blocking Overlay */}
      {blockingInProgress && (
        <div className="bus-blocking-overlay">
          <div className="bus-blocking-spinner"></div>
          <div className="bus-blocking-text">Confirming Your Booking</div>
          <div className="bus-blocking-subtext">
            Please wait while we confirm your booking. This may take a few
            moments.
          </div>
        </div>
      )}
    </div>
  );
};

export default BusSeatSelection;
